/**
 * Stakeholders Page Component
 * Lists all stakeholders for the authenticated client
 * Features: Search by name/affiliation/bio, filter by category, sort by multiple criteria
 * Enhanced: Uniform card sizes, relationship/influence/interest badges, sorting
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { auth } from '../auth';
import StakeholderSearch from '../components/StakeholderSearch';
import StakeholderRecommendations from '../components/StakeholderRecommendations';
import './Stakeholders.css';

interface Stakeholder {
  id: string;
  name: string;
  profile_image: string | null;
  title: string | null;
  category: string | null;
  affiliation: string | null;
  bio: string | null;
  relationship: string | null;
  influence_level: string | null;
  interest_level: string | null;
}

// All stakeholder categories matching admin portal
const CATEGORIES = [
  'All Categories',
  'Government',
  'Traditional',
  'Regulator',
  'Media',
  'Employee',
  'Investor',
  'Politician',
  'NGO',
  'Trade Union',
  'Industry Association',
  'Academic',
  'Community Leader',
  'Supplier',
  'Customer',
  'Competitor',
  'Legal/Compliance',
  'Other'
];

// Sort options for stakeholder list
const SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name (A-Z)' },
  { value: 'name-desc', label: 'Name (Z-A)' },
  { value: 'influence-high', label: 'Influence (High to Low)' },
  { value: 'influence-low', label: 'Influence (Low to High)' },
  { value: 'interest-high', label: 'Interest (High to Low)' },
  { value: 'interest-low', label: 'Interest (Low to High)' },
  { value: 'relationship', label: 'Relationship (Priority)' }
];

const Stakeholders: React.FC = () => {
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [sortBy, setSortBy] = useState('name-asc');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [activeTab, setActiveTab] = useState<'stakeholders' | 'recommendations'>('stakeholders');

  useEffect(() => {
    loadStakeholders();
  }, []);

  const loadStakeholders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getStakeholders();
      setStakeholders(response.stakeholders);
    } catch (err: any) {
      setError(err.message || 'Failed to load stakeholders');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      
      // Build query parameters from current filters
      const params = new URLSearchParams();
      if (selectedCategory !== 'All Categories') {
        params.append('category', selectedCategory);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Build headers with auth and impersonation for admin users
      const headers: Record<string, string> = {
        ...auth.getAuthHeader()
      };
      
      // For admin users, add the impersonated client ID header
      if (auth.isAdminPortalUser()) {
        const selectedClientId = auth.getClientId();
        if (selectedClientId) {
          headers['X-Impersonated-Client-ID'] = selectedClientId;
        }
      }
      
      // Make API request
      const response = await fetch(
        `/api/portal/stakeholders/export/csv?${params}`,
        { headers }
      );
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stakeholders_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export stakeholders. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setExportingPdf(true);
      
      // Build query parameters from current filters
      const params = new URLSearchParams();
      if (selectedCategory !== 'All Categories') {
        params.append('category', selectedCategory);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Build headers with auth and impersonation for admin users
      const headers: Record<string, string> = {
        ...auth.getAuthHeader()
      };
      
      // For admin users, add the impersonated client ID header
      if (auth.isAdminPortalUser()) {
        const selectedClientId = auth.getClientId();
        if (selectedClientId) {
          headers['X-Impersonated-Client-ID'] = selectedClientId;
        }
      }
      
      // Make API request
      const response = await fetch(
        `/api/portal/stakeholders/export/pdf?${params}`,
        { headers }
      );
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stakeholders_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('PDF Export failed:', error);
      alert('Failed to export stakeholders PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  // Sorting function with multi-level logic
  const sortStakeholders = (stakeholders: Stakeholder[], sortOption: string): Stakeholder[] => {
    const sorted = [...stakeholders];
    
    const influenceOrder: { [key: string]: number } = { 'High': 3, 'Medium': 2, 'Low': 1 };
    const interestOrder: { [key: string]: number } = { 'High': 3, 'Medium': 2, 'Low': 1 };
    const relationshipOrder: { [key: string]: number } = {
      'opposed': 4,
      'hostile': 4,
      'supportive': 3,
      'champion': 3,
      'neutral': 2,
      'unknown': 1
    };
    
    switch(sortOption) {
      case 'name-asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
        
      case 'name-desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
        
      case 'influence-high':
        return sorted.sort((a, b) => {
          const aVal = influenceOrder[a.influence_level || ''] || 0;
          const bVal = influenceOrder[b.influence_level || ''] || 0;
          if (bVal !== aVal) return bVal - aVal;
          return a.name.localeCompare(b.name);
        });
        
      case 'influence-low':
        return sorted.sort((a, b) => {
          const aVal = influenceOrder[a.influence_level || ''] || 0;
          const bVal = influenceOrder[b.influence_level || ''] || 0;
          if (aVal !== bVal) return aVal - bVal;
          return a.name.localeCompare(b.name);
        });
        
      case 'interest-high':
        return sorted.sort((a, b) => {
          const aVal = interestOrder[a.interest_level || ''] || 0;
          const bVal = interestOrder[b.interest_level || ''] || 0;
          if (bVal !== aVal) return bVal - aVal;
          return a.name.localeCompare(b.name);
        });
        
      case 'interest-low':
        return sorted.sort((a, b) => {
          const aVal = interestOrder[a.interest_level || ''] || 0;
          const bVal = interestOrder[b.interest_level || ''] || 0;
          if (aVal !== bVal) return aVal - bVal;
          return a.name.localeCompare(b.name);
        });
        
      case 'relationship':
        return sorted.sort((a, b) => {
          // Primary sort: by relationship priority
          const aRel = relationshipOrder[a.relationship?.toLowerCase() || ''] || 0;
          const bRel = relationshipOrder[b.relationship?.toLowerCase() || ''] || 0;
          if (bRel !== aRel) return bRel - aRel;
          
          // Secondary sort: by influence level
          const aInf = influenceOrder[a.influence_level || ''] || 0;
          const bInf = influenceOrder[b.influence_level || ''] || 0;
          if (bInf !== aInf) return bInf - aInf;
          
          // Tertiary sort: by name
          return a.name.localeCompare(b.name);
        });
        
      default:
        return sorted;
    }
  };

  // Filter stakeholders by search term (name, affiliation, bio) and category
  const filteredStakeholders = stakeholders.filter((stakeholder) => {
    // Category filter
    const categoryMatch = selectedCategory === 'All Categories' || 
                         stakeholder.category === selectedCategory;
    
    if (!categoryMatch) return false;
    
    // Search filter (case-insensitive, searches name, affiliation, and bio)
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = stakeholder.name.toLowerCase().includes(searchLower);
    const affiliationMatch = stakeholder.affiliation?.toLowerCase().includes(searchLower) || false;
    const bioMatch = stakeholder.bio?.toLowerCase().includes(searchLower) || false;
    
    return nameMatch || affiliationMatch || bioMatch;
  });

  // Apply sorting to filtered results with memoization for performance
  const sortedStakeholders = useMemo(() => {
    return sortStakeholders(filteredStakeholders, sortBy);
  }, [filteredStakeholders, sortBy]);

  if (loading) {
    return (
      <div className="stakeholders-loading">
        <div className="spinner"></div>
        <p>Loading stakeholders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stakeholders-error">
        <p>❌ {error}</p>
        <button onClick={loadStakeholders}>Retry</button>
      </div>
    );
  }

  return (
    <div className="stakeholders-page">
      <div className="stakeholders-header">
        <div>
          <h1>Stakeholders</h1>
          <p className="stakeholders-subtitle">
            {filteredStakeholders.length} {filteredStakeholders.length === 1 ? 'stakeholder' : 'stakeholders'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="export-csv-btn"
            onClick={handleExportCSV}
            disabled={exporting || sortedStakeholders.length === 0}
            title="Export stakeholders to CSV"
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          <button 
            className="export-pdf-btn"
            onClick={handleExportPDF}
            disabled={exportingPdf || sortedStakeholders.length === 0}
            title="Export stakeholders to PDF"
          >
            {exportingPdf ? 'Exporting...' : 'Export PDF'}
          </button>
          <button 
            className="add-from-directory-btn"
            onClick={() => setShowSearchModal(true)}
            title="Add stakeholder from global directory"
          >
            <span className="btn-icon">+</span>
            <span className="btn-text">Add from Directory</span>
          </button>
        </div>
      </div>


      {/* Tabs */}
      <div className="stakeholders-tabs">
        <button
          className={`tab-button ${activeTab === 'stakeholders' ? 'active' : ''}`}
          onClick={() => setActiveTab('stakeholders')}
        >
          👥 Stakeholders
        </button>
        <button
          className={`tab-button ${activeTab === 'recommendations' ? 'active' : ''}`}
          onClick={() => setActiveTab('recommendations')}
        >
          ✨ Recommendations
        </button>
      </div>

      {/* Stakeholders Tab Content */}
      {activeTab === 'stakeholders' && (
        <>
      {/* Search and Filter Controls */}
      <div className="stakeholders-controls">
        <input
          type="text"
          placeholder="Search by name, affiliation, or bio..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        
        <div className="filters-row">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-filter"
            aria-label="Filter by category"
          >
            {CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-dropdown"
            aria-label="Sort stakeholders by"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stakeholders list */}
      {sortedStakeholders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>No stakeholders found</h3>
          <p>
            {searchTerm || selectedCategory !== 'All Categories'
              ? 'Try adjusting your search term or filter.'
              : 'There are no stakeholders associated with your client.'}
          </p>
        </div>
      ) : (
        <div className="stakeholders-grid">
          {sortedStakeholders.map((stakeholder, index) => (
            <Link 
              key={stakeholder.id} 
              to={`/stakeholders/${stakeholder.id}`}
              className="stakeholder-card-link"
              {...(index === 0 ? { 'data-tour': 'stakeholder-card-first' } : {})}
            >
              <div className="stakeholder-card">
                <div className="stakeholder-avatar">
                  {stakeholder.profile_image ? (
                    <img src={stakeholder.profile_image} alt={stakeholder.name} />
                  ) : (
                    stakeholder.name.charAt(0).toUpperCase()
                  )}
                </div>
                
                <div className="stakeholder-info">
                  <h3 className="stakeholder-name">{stakeholder.name}</h3>
                  
                  {stakeholder.affiliation && (
                    <p className="stakeholder-affiliation">
                      {stakeholder.affiliation}
                    </p>
                  )}
                  
                  {/* Enhanced metadata section matching admin portal */}
                  <div className="stakeholder-metadata">
                    {stakeholder.influence_level && (
                      <div className="metadata-row">
                        <span className="metadata-label">Influence:</span>
                        <span className={`metadata-badge influence-${stakeholder.influence_level?.toLowerCase()}`}>
                          {stakeholder.influence_level}
                        </span>
                      </div>
                    )}
                    
                    {stakeholder.interest_level && (
                      <div className="metadata-row">
                        <span className="metadata-label">Interest:</span>
                        <span className={`metadata-badge interest-${stakeholder.interest_level?.toLowerCase()}`}>
                          {stakeholder.interest_level}
                        </span>
                      </div>
                    )}
                    
                    {stakeholder.relationship && (
                      <div className="metadata-row">
                        <span className="metadata-label">Relationship:</span>
                        <span className={`metadata-badge relationship-${stakeholder.relationship?.toLowerCase()}`}>
                          {stakeholder.relationship}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

        </>
      )}

      {/* Recommendations Tab Content */}
      {activeTab === 'recommendations' && (
        <StakeholderRecommendations />
      )}
      {/* Stakeholder Search Modal */}
      {showSearchModal && (
        <StakeholderSearch
          onClose={() => setShowSearchModal(false)}
          onStakeholderAdded={() => {
            setShowSearchModal(false);
            loadStakeholders(); // Refresh the list
          }}
        />
      )}
    </div>
  );
};

export default Stakeholders;
